import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Anchor, Button, Card, Group, Loader, Stack, TextInput, Title, Switch, Tabs, Box } from '@mantine/core';
import { IconArrowLeft, IconEye, IconPencil } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { fetchTemplate, saveTemplate, type TemplateRow } from '@/features/templates/api';
import { TiptapEditor } from '@/features/templates/TiptapEditor';

export const Route = createFileRoute('/_app/templates/$templateId')({ component: TemplateDetailPage });

function TemplateDetailPage() {
  const { templateId } = useParams({ from: '/_app/templates/$templateId' });
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['template', templateId], queryFn: () => fetchTemplate(templateId) });

  const [draft, setDraft] = useState<Partial<TemplateRow>>({});
  useEffect(() => {
    if (data) setDraft({ name: data.name, subject: data.subject, body: data.body, isActive: data.isActive });
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => saveTemplate(templateId, draft),
    onSuccess: () => {
      notifications.show({ color: 'green', message: 'Template salvato' });
      qc.invalidateQueries({ queryKey: ['template', templateId] });
    },
    onError: () => notifications.show({ color: 'red', message: 'Errore' }),
  });

  if (isLoading || !data) return <Loader />;

  const renderedPreview = (draft.body ?? '').replace(/\{\{(\w+)\}\}/g, (_m, key: string) => {
    const dummy: Record<string, string> = { userName: 'Mario Rossi', tenantName: 'Demo Inc', date: new Date().toLocaleDateString('it-IT') };
    return dummy[key] ?? `{{${key}}}`;
  });

  return (
    <Stack>
      <Anchor component={Link} to="/templates" size="sm">
        <Group gap={4}><IconArrowLeft size={14} /> Torna ai Template</Group>
      </Anchor>
      <Group justify="space-between">
        <Title order={1}>{data.name}</Title>
        <Group>
          <Switch
            label="Attivo"
            checked={draft.isActive ?? false}
            onChange={(e) => setDraft((d) => ({ ...d, isActive: e.currentTarget.checked }))}
          />
          <Button onClick={() => void save.mutate()} loading={save.isPending}>Salva</Button>
        </Group>
      </Group>
      <Card withBorder padding="lg" radius="md">
        <Stack>
          <TextInput
            label="Nome interno"
            value={draft.name ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.currentTarget.value }))}
          />
          <TextInput
            label="Oggetto email"
            value={draft.subject ?? ''}
            onChange={(e) => setDraft((d) => ({ ...d, subject: e.currentTarget.value }))}
          />
        </Stack>
      </Card>
      <Tabs defaultValue="editor">
        <Tabs.List>
          <Tabs.Tab value="editor" leftSection={<IconPencil size={16} />}>Editor</Tabs.Tab>
          <Tabs.Tab value="preview" leftSection={<IconEye size={16} />}>Anteprima</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="editor" pt="md">
          <TiptapEditor value={draft.body ?? ''} onChange={(html) => setDraft((d) => ({ ...d, body: html }))} />
        </Tabs.Panel>
        <Tabs.Panel value="preview" pt="md">
          <Card withBorder padding="lg" radius="md">
            <Box dangerouslySetInnerHTML={{ __html: renderedPreview }} />
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
